// Family Account settings (Settings page card): defaults, ranges and the
// pilot allowlist parser. Defaults and ranges mirror epoint-be's
// AppSettings schema and UpdateAppSettingsDto (D8).

import type { AppSettings } from "../services/api";

export const FAMILY_SETTING_DEFAULTS = {
  familyAccountEnabled: false,
  familyMemberLoginEnabled: true,
  familyMaxMembers: 6,
  familyInviteTtlDays: 15,
  familyInviteDailyLimit: 10,
} as const;

export type FamilyIntegerSettingKey =
  | "familyMaxMembers"
  | "familyInviteTtlDays"
  | "familyInviteDailyLimit";

export interface FamilyIntegerSettingRule {
  label: string;
  min: number;
  max: number;
  hint: string;
}

export const FAMILY_INTEGER_SETTING_RULES: Record<
  FamilyIntegerSettingKey,
  FamilyIntegerSettingRule
> = {
  familyMaxMembers: {
    label: "Max members",
    min: 2,
    max: 6,
    hint: "Seats including the admin. Pending invitations hold a seat.",
  },
  familyInviteTtlDays: {
    label: "Invitation validity (days)",
    min: 1,
    max: 60,
    hint: "Days an invitation stays open before it expires.",
  },
  familyInviteDailyLimit: {
    label: "Daily invitation limit",
    min: 1,
    max: 100,
    hint: "New invitations one admin may send per UTC day.",
  },
};

/** epoint-be limits for the allowlist (ArrayMaxSize, MaxLength each). */
export const FAMILY_ALLOWLIST_MAX_ENTRIES = 500;
export const FAMILY_ALLOWLIST_MAX_ID_LENGTH = 64;

/**
 * The whole number the input holds, or null when it is not a whole number
 * inside the rule's range.
 */
export function parseFamilyIntegerSetting(
  key: FamilyIntegerSettingKey,
  value: string,
): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  const rule = FAMILY_INTEGER_SETTING_RULES[key];
  if (!Number.isSafeInteger(parsed) || parsed < rule.min || parsed > rule.max) {
    return null;
  }
  return parsed;
}

/** Warning text for an out-of-range or non-integer value. */
export function familyIntegerSettingWarning(
  key: FamilyIntegerSettingKey,
): string {
  const rule = FAMILY_INTEGER_SETTING_RULES[key];
  const suffix = key === "familyMaxMembers" ? " (including the admin)" : "";
  return `${rule.label} must be a whole number between ${rule.min} and ${rule.max}${suffix}.`;
}

/**
 * Splits the allowlist editor text on commas and whitespace (newlines
 * included), trims and uppercases every entry, drops blanks and keeps the
 * first occurrence of each id. epoint-be (FamilySettingsService.fromRaw)
 * trims and uppercases entries too and compares case-insensitively, so
 * "cc100" and "CC100" are one id.
 */
export function parseFamilyAllowlist(text: string): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const part of text.split(/[\s,]+/)) {
    const id = part.trim().toUpperCase();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/** Entries that do not look like a customer id ("CC…"). Only a hint. */
export function nonCustomerIdEntries(ids: string[]): string[] {
  return ids.filter((id) => !/^CC/i.test(id));
}

/** Blocking problem that epoint-be would refuse, or null. */
export function familyAllowlistError(ids: string[]): string | null {
  if (ids.length > FAMILY_ALLOWLIST_MAX_ENTRIES) {
    return `The allowlist can hold at most ${FAMILY_ALLOWLIST_MAX_ENTRIES} customer ids.`;
  }
  if (ids.some((id) => id.length > FAMILY_ALLOWLIST_MAX_ID_LENGTH)) {
    return `A customer id can be at most ${FAMILY_ALLOWLIST_MAX_ID_LENGTH} characters.`;
  }
  return null;
}

/**
 * The allowlist from a GET /app-settings answer, or null when the answer
 * has no usable list. epoint-be strips the key for callers it does not
 * recognise as admin, so a missing key means "unknown", never "empty".
 */
export function readFamilyAllowlist(
  settings: Partial<AppSettings> | null | undefined,
): string[] | null {
  if (
    !settings ||
    typeof settings !== "object" ||
    !Object.prototype.hasOwnProperty.call(settings, "familyAllowlistCustomerIds")
  ) {
    return null;
  }
  const list = settings.familyAllowlistCustomerIds;
  if (!Array.isArray(list)) return null;
  return list.filter((id): id is string => typeof id === "string");
}

/** One id per line, the editor's display format. */
export function formatFamilyAllowlist(ids: string[]): string {
  return ids.join("\n");
}
